import React from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadFile } from '../utils/downloadFile';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from './ui/context-menu';
import { playTrack, useGlobalPlayer, togglePlay } from './GlobalMusicPlayer';
import { useAuth } from '../contexts/AuthContext';
import {
  Play, Pause, Download, Trash2, Eye, EyeOff,
  Scissors, Mic, Wand2, Maximize2, SendHorizonal
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SongContextMenu = ({ song, onDelete, onRefresh, children }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { currentTrack, isPlaying } = useGlobalPlayer();
  const headers = { Authorization: `Bearer ${token}` };

  const isCurrentlyPlaying = currentTrack?.id === song?.id && isPlaying;

  const handlePlayPause = () => {
    if (currentTrack?.id === song?.id) {
      togglePlay();
    } else {
      playTrack({
        id: song.id,
        title: song.title,
        artist: song.artist || song.created_by,
        audio_url: song.audio_url || song.file_url,
        album_cover: song.cover_image || song.album_cover,
      });
    }
  };

  const handleDownload = () => {
    const url = song.audio_url || song.file_url;
    if (!url) {
      toast.error('No audio URL available');
      return;
    }
    downloadFile(url, `${song.title || 'track'}.mp3`);
  };

  const handleDelete = async () => {
    try {
      if (song.source === 'ai') {
        await axios.delete(`${API}/ai/library/${song.id}`, { headers });
      } else {
        await axios.delete(`${API}/music/${song.id}`, { headers });
      }
      toast.success('Moved to trash');
      if (onDelete) onDelete(song.id);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete track');
    }
  };

  const handleToggleVisibility = async () => {
    try {
      const newVisibility = !song.is_public;
      if (song.source === 'ai') {
        await axios.put(`${API}/ai/library/${song.id}/visibility`, { is_public: newVisibility }, { headers });
      } else {
        await axios.put(`${API}/music/${song.id}/visibility`, { is_public: newVisibility }, { headers });
      }
      toast.success(newVisibility ? 'Track is now public' : 'Track is now private');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const handleSendToStudio = (action) => {
    const audioUrl = encodeURIComponent(song.audio_url || song.file_url || '');
    navigate(`/admin/ai-studio?action=${action}&audioUrl=${audioUrl}`);
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent
        className="min-w-[200px] rounded-xl border backdrop-blur-xl"
        style={{
          backgroundColor: 'rgba(10, 10, 20, 0.95)',
          borderColor: 'rgba(0, 240, 255, 0.2)',
          boxShadow: '0 0 20px rgba(0, 240, 255, 0.1)',
        }}
      >
        <ContextMenuItem
          onClick={handlePlayPause}
          className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
        >
          {isCurrentlyPlaying ? (
            <><Pause className="w-4 h-4 text-cyan-400" /> Pause</>
          ) : (
            <><Play className="w-4 h-4 text-cyan-400" /> Play</>
          )}
        </ContextMenuItem>

        <ContextMenuItem
          onClick={handleDownload}
          className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
        >
          <Download className="w-4 h-4 text-cyan-400" />
          Download
        </ContextMenuItem>

        <ContextMenuSeparator className="bg-white/10" />

        <ContextMenuSub>
          <ContextMenuSubTrigger className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer">
            <SendHorizonal className="w-4 h-4 text-cyan-400" />
            Send to AI Studio
          </ContextMenuSubTrigger>
          <ContextMenuSubContent
            className="min-w-[160px] rounded-xl border backdrop-blur-xl"
            style={{
              backgroundColor: 'rgba(10, 10, 20, 0.95)',
              borderColor: 'rgba(0, 240, 255, 0.2)',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.1)',
            }}
          >
            <ContextMenuItem
              onClick={() => handleSendToStudio('cover')}
              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
            >
              <Mic className="w-4 h-4 text-purple-400" />
              AI Cover
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => handleSendToStudio('stems')}
              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
            >
              <Scissors className="w-4 h-4 text-green-400" />
              Extract Stems
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => handleSendToStudio('master')}
              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
            >
              <Wand2 className="w-4 h-4 text-yellow-400" />
              Master Track
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => handleSendToStudio('extend')}
              className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
            >
              <Maximize2 className="w-4 h-4 text-blue-400" />
              Extend Music
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSeparator className="bg-white/10" />

        <ContextMenuItem
          onClick={handleToggleVisibility}
          className="flex items-center gap-2 text-gray-200 hover:text-white focus:bg-white/10 focus:text-white cursor-pointer"
        >
          {song.is_public ? (
            <><EyeOff className="w-4 h-4 text-orange-400" /> Make Private</>
          ) : (
            <><Eye className="w-4 h-4 text-green-400" /> Make Public</>
          )}
        </ContextMenuItem>

        <ContextMenuItem
          onClick={handleDelete}
          className="flex items-center gap-2 text-red-400 hover:text-red-300 focus:bg-red-500/10 focus:text-red-300 cursor-pointer"
        >
          <Trash2 className="w-4 h-4" />
          Move to Trash
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default SongContextMenu;
